import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Camera, CheckCircle2, ImagePlus, Info, Loader2, Trash2, X } from "lucide-react";
import { api, errMsg, media, GENDER_LABEL, METHOD_OPTION_LABEL, SERVICE_LABEL } from "@/lib/api";
import { WhatsAppIcon } from "@/components/lux/Icons";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function ReviewBanner({ data }) {
  if (!data) return null;
  const map = {
    in_review: { tone: "warn", title: "In review", text: "A moderator is reviewing your submission. You can keep working - your approved version is still visible." },
    changes_requested: { tone: "warn", title: "Changes requested", text: data.review_reason || "A moderator asked for changes." },
    rejected: { tone: "error", title: "Not published", text: data.review_reason || "Your latest submission wasn't accepted." },
    none: null,
  };
  const info = map[data.review_state];
  if (!info) return null;
  const colors = info.tone === "warn"
    ? "border-lux-warning/35 bg-lux-warning/5 text-lux-warning"
    : "border-lux-error/35 bg-lux-error/5 text-lux-error";
  return (
    <div className={`lux-card flex items-start gap-3 border p-5 ${colors}`} data-testid="listing-review-banner">
      {info.tone === "warn" ? <Info className="mt-1 h-5 w-5 shrink-0" /> : <AlertTriangle className="mt-1 h-5 w-5 shrink-0" />}
      <div>
        <p className="font-serif text-lg text-lux-text">{info.title}</p>
        <p className="mt-1 text-sm text-lux-text/80">{info.text}</p>
        {data.case?.number && <p className="mt-1 text-xs text-lux-muted">Case {data.case.number}</p>}
      </div>
    </div>
  );
}

function PhotoTile({ photo, onDelete }) {
  const stateMap = {
    approved: ["Approved", "text-lux-success border-lux-success/40"],
    pending: ["In review", "text-lux-warning border-lux-warning/40"],
    rejected: ["Rejected", "text-lux-error border-lux-error/40"],
  };
  const [label, cls] = stateMap[photo.status] || [photo.status, "text-lux-muted border-lux-border"];
  return (
    <div className="relative overflow-hidden rounded-[12px] border border-lux-border bg-lux-surface2" data-testid="listing-editor-photo-slot">
      <div className="aspect-[3/4]">
        <img src={media(photo.url)} alt="Your uploaded photo" className="h-full w-full object-cover" />
      </div>
      <span className={`absolute left-2 top-2 inline-flex items-center rounded-full border bg-black/70 px-2 py-0.5 text-[10px] font-medium uppercase ${cls}`} data-testid="photo-status">
        {label}
      </span>
      <button onClick={() => onDelete(photo.id)}
        className="absolute right-2 top-2 rounded-full bg-black/70 p-1.5 text-lux-silver hover:text-lux-silverBright"
        aria-label="Remove photo" data-testid="listing-editor-photo-delete">
        <X className="h-3.5 w-3.5" />
      </button>
      {photo.status === "rejected" && photo.reject_reason && (
        <p className="absolute inset-x-0 bottom-0 bg-black/80 p-2 text-[11px] text-lux-error">{photo.reject_reason}</p>
      )}
    </div>
  );
}

const EMPTY_FORM = {
  display_name: "",
  age: 25,
  headline: "",
  description: "",
  gender: "",
  ethnicity: "prefer-not",
  service_types: [],
  primary_area: "",
  nearby_areas: [],
  contact_method: "phone",
  contact_value: "",
  price_min: "",
  price_max: "",
};

function fromContent(c) {
  if (!c) return null;
  return {
    display_name: c.display_name || "",
    age: c.age || 25,
    headline: c.headline || "",
    description: c.description || "",
    gender: c.gender || "",
    ethnicity: c.ethnicity || "prefer-not",
    service_types: c.service_types || [],
    primary_area: c.primary_area || "",
    nearby_areas: c.nearby_areas || [],
    contact_method: c.contact_method || "phone",
    contact_value: c.contact_value || "",
    price_min: c.price_min ?? "",
    price_max: c.price_max ?? "",
  };
}

export default function ListingEditorPage() {
  useSeo({ title: "Listing editor | LUX", ...PRIVATE_SEO });
  const client = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["advertiser", "listing"],
    queryFn: async () => (await api.get("/advertiser/listing")).data,
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [city, setCity] = useState("");
  const [initialised, setInitialised] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState("");
  const [uploadErr, setUploadErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (data && !initialised) {
      const base = fromContent(data.pending) || fromContent(data.approved) || EMPTY_FORM;
      setForm((prev) => ({ ...prev, ...base }));
      const areaCity = (data.areas || []).find((a) => a.slug === base.primary_area)?.city;
      setCity(areaCity || data.city || data.default_city || data.cities?.[0]?.slug || "");
      setInitialised(true);
    }
  }, [data, initialised]);

  const selectedPhotoIds = useMemo(
    () => (data?.photos || []).filter((p) => p.status !== "rejected" && p.status !== "removed").map((p) => p.id),
    [data?.photos],
  );

  if (isLoading || !data) {
    return <div className="text-sm text-lux-muted" data-testid="listing-loading">Loading your listing...</div>;
  }

  const needsVerify = !data.verification?.verified;
  if (needsVerify) {
    return (
      <section className="lux-card p-6" data-testid="listing-needs-verification">
        <p className="lux-label">Before you can publish</p>
        <h1 className="mt-2 font-serif text-3xl text-lux-text">Complete verification first</h1>
        <p className="mt-2 text-sm text-lux-muted">LUX requires every advertiser to pass identity and age verification before creating a listing.</p>
        <Link to="/advertiser/verify" className="lux-btn lux-btn-primary mt-5" data-testid="listing-start-verification">Start verification</Link>
      </section>
    );
  }

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const cityAreas = (data.cities || []).find((c) => c.slug === city)?.areas || [];
  const changeCity = (slug) => {
    if (slug === city) return;
    setCity(slug);
    update({ primary_area: "", nearby_areas: [] });
  };
  const toggleService = (slug) => {
    const next = form.service_types.includes(slug)
      ? form.service_types.filter((s) => s !== slug)
      : [...form.service_types, slug];
    update({ service_types: next });
  };
  const toggleNearby = (slug) => {
    if (slug === form.primary_area) return;
    const has = form.nearby_areas.includes(slug);
    if (!has && form.nearby_areas.length >= 2) return;
    update({ nearby_areas: has ? form.nearby_areas.filter((s) => s !== slug) : [...form.nearby_areas, slug] });
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadErr("");
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/advertiser/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        setUploadErr(errMsg(err));
        break;
      }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
    await refetch();
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/advertiser/photos/${id}`);
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr("");
    if (!selectedPhotoIds.length) {
      setSubmitErr("Add at least one photo (not rejected).");
      return;
    }
    const hasMin = form.price_min !== "" && form.price_min !== null;
    const hasMax = form.price_max !== "" && form.price_max !== null;
    if (hasMin !== hasMax) {
      setSubmitErr("Add both a minimum and maximum price, or leave both blank.");
      return;
    }
    if (hasMin && hasMax && Number(form.price_min) > Number(form.price_max)) {
      setSubmitErr("The minimum price can't be higher than the maximum price.");
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/advertiser/listing", {
        ...form,
        age: Number(form.age) || 18,
        service_types: form.service_types,
        nearby_areas: form.nearby_areas,
        photo_ids: selectedPhotoIds,
        price_min: hasMin ? Number(form.price_min) : null,
        price_max: hasMax ? Number(form.price_max) : null,
      });
      toast.success("Submitted for review");
      client.invalidateQueries({ queryKey: ["advertiser"] });
      await refetch();
    } catch (err) {
      setSubmitErr(errMsg(err));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleAvailable = async (v) => {
    try {
      await api.patch("/advertiser/availability", { available_today: v });
      toast.success(v ? "Marked as available today" : "Hidden availability badge");
      await refetch();
      client.invalidateQueries({ queryKey: ["advertiser"] });
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const headlineLen = form.headline.length;
  const descLen = form.description.length;
  const headlineOver = headlineLen > 60;
  const descOver = descLen > 1500;

  return (
    <div className="space-y-6" data-testid="listing-editor">
      <header>
        <p className="lux-label">Listing</p>
        <h1 className="mt-2 font-serif text-4xl text-lux-text sm:text-5xl">Edit your listing</h1>
        <p className="mt-3 max-w-2xl text-sm text-lux-muted">
          Every change goes to a moderator before it goes live. Your approved version stays visible while edits are reviewed.
        </p>
      </header>

      <ReviewBanner data={{ ...data, case: data.case }} />

      {data.state === "live" && (
        <section className="lux-card flex items-center justify-between p-5" data-testid="listing-availability-card">
          <div>
            <p className="lux-label">Availability</p>
            <p className="mt-2 text-sm text-lux-text/90">Show an "Available today" badge on your profile.</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!data.available_today} onCheckedChange={toggleAvailable} data-testid="listing-editor-available-today-switch" />
            <span className="text-sm text-lux-silver">{data.available_today ? "On" : "Off"}</span>
          </div>
        </section>
      )}

      <form onSubmit={submit} className="space-y-6" noValidate>
        {/* Photos */}
        <section className="lux-card p-5 sm:p-6" data-testid="listing-editor-photos-section">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="lux-label">Photos</p>
              <p className="mt-2 font-serif text-xl text-lux-text">Up to {data.max_photos} photos</p>
              <p className="mt-1 text-sm text-lux-muted">Non-explicit only. Your own photos. No third-party watermarks or text on the image.</p>
              <p className="mt-2 flex items-start gap-2 text-sm text-lux-text/85" data-testid="listing-editor-watermark-note">
                <span className="mt-0.5 inline-block h-4 w-4 shrink-0 rounded-sm border border-lux-border bg-lux-surface2 text-center text-[9px] font-bold leading-4 text-lux-silverBright" aria-hidden="true">L</span>
                <span>Every photo is checked automatically on upload (nudity, third-party watermarks, possible minors, duplicates) and then published with a discreet <span className="text-lux-text">LUX watermark at 50% opacity</span> to protect it from being copied elsewhere. The clean copy is seen only by moderators.</span>
              </p>
            </div>
            <label className="lux-btn lux-btn-primary lux-btn-sm cursor-pointer" data-testid="listing-editor-photo-upload-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {uploading ? "Uploading..." : "Add photo"}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="sr-only"
                onChange={upload} data-testid="listing-editor-photo-upload-input" />
            </label>
          </div>
          {uploadErr && <p className="mt-3 text-sm text-lux-error" role="alert" data-testid="listing-editor-photo-error">{uploadErr}</p>}
          {(data.photos || []).length === 0 ? (
            <div className="mt-5 flex flex-col items-center rounded-[12px] border border-dashed border-lux-border bg-lux-surface2 px-6 py-12 text-center" data-testid="listing-editor-photos-empty">
              <Camera className="h-8 w-8 text-lux-silver" aria-hidden="true" />
              <p className="mt-4 font-serif text-lg text-lux-text">No photos yet</p>
              <p className="mt-1 max-w-sm text-sm text-lux-muted">Add discreet, non-explicit photos. Our automatic check will flag watermarks or nudity immediately.</p>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {data.photos.map((p) => <PhotoTile key={p.id} photo={p} onDelete={removePhoto} />)}
            </div>
          )}
        </section>

        {/* Basics */}
        <section className="lux-card space-y-5 p-5 sm:p-6">
          <div>
            <p className="lux-label">Basics</p>
            <h2 className="mt-2 font-serif text-xl text-lux-text">About you</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
            <div>
              <label htmlFor="display-name" className="lux-label">Display name</label>
              <input id="display-name" required maxLength={30} className="lux-input mt-2" value={form.display_name}
                onChange={(e) => update({ display_name: e.target.value })} placeholder="The first name visitors will see"
                data-testid="listing-editor-display-name-input" />
            </div>
            <div>
              <label htmlFor="age" className="lux-label">Age</label>
              <input id="age" type="number" required min={18} max={80} className="lux-input mt-2" value={form.age}
                onChange={(e) => update({ age: e.target.value })} data-testid="listing-editor-age-input" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="headline" className="lux-label">Headline</label>
              <span className={`text-xs ${headlineOver ? "text-lux-error" : "text-lux-muted"}`} data-testid="listing-editor-headline-counter">
                {headlineLen}/60
              </span>
            </div>
            <input id="headline" required maxLength={60} className="lux-input mt-2" value={form.headline}
              onChange={(e) => update({ headline: e.target.value })} placeholder="A short, factual line"
              data-testid="listing-editor-headline-input" />
            <p className="mt-1 text-xs text-lux-muted">No emojis. No prices or acts.</p>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="description" className="lux-label">Description</label>
              <span className={`text-xs ${descOver ? "text-lux-error" : "text-lux-muted"}`}>{descLen}/1500</span>
            </div>
            <textarea id="description" rows={6} required maxLength={1500} className="lux-input mt-2" value={form.description}
              onChange={(e) => update({ description: e.target.value })} placeholder="Tell visitors who you are. Keep it discreet and your own."
              data-testid="listing-editor-description-textarea" />
          </div>
        </section>

        {/* Category & services */}
        <section className="lux-card space-y-5 p-5 sm:p-6">
          <div>
            <p className="lux-label">Category</p>
            <h2 className="mt-2 font-serif text-xl text-lux-text">How you'd like to be listed</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="lux-label">Category</label>
              <Select key={initialised ? "gender-ready" : "gender-init"} value={form.gender || undefined} onValueChange={(v) => update({ gender: v })}>
                <SelectTrigger className="lux-input mt-2 h-11 justify-between" data-testid="listing-editor-gender-select"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(GENDER_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="lux-label">Ethnicity (optional)</label>
              <Select key={initialised ? "eth-ready" : "eth-init"} value={form.ethnicity || "prefer-not"} onValueChange={(v) => update({ ethnicity: v })}>
                <SelectTrigger className="lux-input mt-2 h-11 justify-between" data-testid="listing-editor-ethnicity-select"><SelectValue placeholder="Prefer not to say" /></SelectTrigger>
                <SelectContent>
                  {(data.ethnicities || []).map((e) => <SelectItem key={e.slug} value={e.slug}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-lux-muted">Self-described. Visitors can filter by it; "Prefer not to say" is never shown.</p>
            </div>
            <div>
              <p className="lux-label">Incall / outcall</p>
              <div className="mt-2 flex gap-2">
                {Object.entries(SERVICE_LABEL).map(([k, l]) => {
                  const active = form.service_types.includes(k);
                  return (
                    <button type="button" key={k} onClick={() => toggleService(k)}
                      className={`flex-1 rounded-[10px] border px-3 py-2.5 text-sm transition-colors ${active ? "border-lux-silver bg-lux-surface2 text-lux-silverBright" : "border-lux-border text-lux-text/85 hover:border-[#3a3a40]"}`}
                      data-testid={`listing-editor-service-${k}`} aria-pressed={active}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="lux-card space-y-5 p-5 sm:p-6" data-testid="listing-editor-pricing-section">
          <div>
            <p className="lux-label">Rates</p>
            <h2 className="mt-2 font-serif text-xl text-lux-text">Your price range (optional)</h2>
            <p className="mt-1 text-sm text-lux-muted">
              Shown as a range on your profile, and visitors can filter by budget. This is your rate for time and companionship only - never describe acts or services.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="price-min" className="lux-label">Minimum (£)</label>
              <div className="relative mt-2">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-lux-muted">£</span>
                <input id="price-min" type="number" min={1} max={10000} inputMode="numeric" className="lux-input pl-7" value={form.price_min}
                  onChange={(e) => update({ price_min: e.target.value })} placeholder="e.g. 150"
                  data-testid="listing-editor-price-min-input" />
              </div>
            </div>
            <div>
              <label htmlFor="price-max" className="lux-label">Maximum (£)</label>
              <div className="relative mt-2">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-lux-muted">£</span>
                <input id="price-max" type="number" min={1} max={10000} inputMode="numeric" className="lux-input pl-7" value={form.price_max}
                  onChange={(e) => update({ price_max: e.target.value })} placeholder="e.g. 300"
                  data-testid="listing-editor-price-max-input" />
              </div>
            </div>
          </div>
          <p className="text-xs text-lux-muted">Leave both fields blank if you'd rather not publish a price.</p>
        </section>


        {/* City & areas */}
        <section className="lux-card space-y-5 p-5 sm:p-6" data-testid="listing-editor-location-section">
          <div>
            <p className="lux-label">Where you list</p>
            <h2 className="mt-2 font-serif text-xl text-lux-text">City and neighbourhoods</h2>
            <p className="mt-1 text-sm text-lux-muted">Choose one city, one primary area and up to two nearby areas in the same city. One profile lists in one city at a time.</p>
          </div>
          <div>
            <p className="lux-label">City</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {(data.cities || []).map((c) => {
                const active = c.slug === city;
                return (
                  <button type="button" key={c.slug} onClick={() => changeCity(c.slug)} aria-pressed={active}
                    className={`rounded-[10px] border px-3 py-2.5 text-sm transition-colors ${active ? "border-lux-silver bg-lux-surface2 text-lux-silverBright" : "border-lux-border text-lux-text/85 hover:border-[#3a3a40]"}`}
                    data-testid={`listing-editor-city-${c.slug}`}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="lux-label">Primary area</label>
            <Select key={`${city}-${cityAreas.length}`} value={form.primary_area || undefined} onValueChange={(v) => update({ primary_area: v, nearby_areas: form.nearby_areas.filter((x) => x !== v) })}>
              <SelectTrigger className="lux-input mt-2 h-11 justify-between" data-testid="listing-editor-primary-area"><SelectValue placeholder="Choose" /></SelectTrigger>
              <SelectContent>
                {cityAreas.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <p className="lux-label">Nearby (optional, max 2)</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {cityAreas.map((a) => {
                const disabled = a.slug === form.primary_area;
                const active = form.nearby_areas.includes(a.slug);
                return (
                  <button type="button" key={a.slug} onClick={() => toggleNearby(a.slug)} disabled={disabled}
                    className={`lux-chip border transition-colors ${active ? "border-lux-silver bg-lux-surface2 text-lux-silverBright" : "border-lux-border text-lux-text/85 hover:border-[#3a3a40]"} ${disabled ? "opacity-40" : ""}`}
                    data-testid={`listing-editor-nearby-${a.slug}`}>
                    {a.name}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="lux-card space-y-5 p-5 sm:p-6">
          <div>
            <p className="lux-label">Contact method</p>
            <h2 className="mt-2 font-serif text-xl text-lux-text">How visitors reach you off-platform</h2>
            <p className="mt-1 text-sm text-lux-muted">LUX has no in-app messaging. We open your preferred app on the visitor's device.</p>
          </div>
          <div className="flex gap-3 rounded-[10px] border border-lux-border bg-lux-surface2 p-4 text-sm text-lux-text/85" data-testid="listing-editor-whatsapp-note">
            <WhatsAppIcon className="mt-0.5 h-4 w-4 shrink-0 text-lux-silver" />
            <p>Visitors always see a <span className="text-lux-text">WhatsApp</span> button when you publish a UK mobile number. WhatsApp opens with a polite, pre-written first message, so you know the contact came from LUX. Choose Telegram only if you don't want a WhatsApp button.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-[230px_1fr]">
            <div>
              <label className="lux-label">Method</label>
              <Select value={form.contact_method} onValueChange={(v) => update({ contact_method: v })}>
                <SelectTrigger className="lux-input mt-2 h-11 justify-between" data-testid="listing-editor-contact-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(METHOD_OPTION_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label htmlFor="contact-value" className="lux-label">{form.contact_method === "telegram" ? "@username" : "UK mobile number (used for WhatsApp)"}</label>
              <input id="contact-value" className="lux-input mt-2" value={form.contact_value}
                onChange={(e) => update({ contact_value: e.target.value })}
                placeholder={form.contact_method === "telegram" ? "@yourname" : "07700 900123"}
                data-testid="listing-editor-contact-value" />
            </div>
          </div>
        </section>

        {submitErr && <p className="text-sm text-lux-error" role="alert" data-testid="listing-editor-submit-error">{submitErr}</p>}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={submitting} className="lux-btn lux-btn-primary" data-testid="listing-editor-save-button">
            {submitting ? "Submitting..." : data.state === "none" ? "Submit for review" : "Submit changes for review"}
          </button>
          <p className="text-xs text-lux-muted">A moderator will review your changes before they appear.</p>
        </div>
      </form>
    </div>
  );
}
